import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type LetterPayload = {
  roleTitle: string;
  employer: string;
  recipient: string;
  date: string;
  salutation: string;
  body: string;
  sign_off: string;
  candidateName: string;
  candidateContact: string;
};

/** Render a styled A4 motivational letter PDF. */
export const generateLetterPdf = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: {
    letter: LetterPayload;
    accentHex?: string;
    filename?: string;
    cvAttachment?: { mime: string; base64: string } | null;
    attachments?: Array<{ mime: string; base64: string; name?: string }> | null;
  }) => {
    if (!input?.letter || typeof input.letter !== "object") throw new Error("Missing letter");
    const l = input.letter;
    const trim = (s: string | undefined, n: number) => (typeof s === "string" ? s.slice(0, n) : "");
    const validateFile = (f: { mime: string; base64: string; name?: string }, label: string) => {
      const mime = String(f.mime || "");
      const b64 = String(f.base64 || "");
      if (!/^(application\/pdf|image\/(png|jpe?g))$/i.test(mime)) {
        throw new Error(`${label} must be a PDF, PNG or JPG`);
      }
      if (b64.length > 20 * 1024 * 1024) throw new Error(`${label} file too large (max ~15MB)`);
      return { mime, base64: b64, name: typeof f.name === "string" ? f.name.slice(0, 200) : undefined };
    };
    let cv: { mime: string; base64: string } | null = null;
    if (input.cvAttachment && typeof input.cvAttachment === "object") {
      const v = validateFile(input.cvAttachment, "CV");
      if (v.base64) cv = { mime: v.mime, base64: v.base64 };
    }
    let atts: Array<{ mime: string; base64: string; name?: string }> = [];
    if (Array.isArray(input.attachments)) {
      if (input.attachments.length > 10) throw new Error("Too many supporting documents (max 10)");
      atts = input.attachments
        .filter((a) => a && typeof a === "object" && a.base64)
        .map((a) => validateFile(a, "Attachment"));
    }
    return {
      letter: {
        roleTitle: trim(l.roleTitle, 200),
        employer: trim(l.employer, 200),
        recipient: trim(l.recipient, 200) || "Hiring Manager",
        date: trim(l.date, 60),
        salutation: trim(l.salutation, 120) || "Dear Hiring Manager,",
        body: trim(l.body, 6000),
        sign_off: trim(l.sign_off, 60) || "Yours sincerely,",
        candidateName: trim(l.candidateName, 160) || "Candidate",
        candidateContact: trim(l.candidateContact, 260),
      },
      accentHex: typeof input.accentHex === "string" ? input.accentHex.slice(0, 7) : "#0B2545",
      filename:
        typeof input.filename === "string"
          ? input.filename.slice(0, 100).replace(/[^a-z0-9_\-. ]/gi, "_")
          : undefined,
      cvAttachment: cv,
      attachments: atts,
    };
  })
  .handler(async ({ data }) => {
    const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
    const { sanitizePdfText } = await import("@/lib/pdf-text");
    const doc = await PDFDocument.create();
    let page = doc.addPage([595.28, 841.89]); // A4 in points
    const { width, height } = page.getSize();
    const font = await doc.embedFont(StandardFonts.Helvetica);
    const bold = await doc.embedFont(StandardFonts.HelveticaBold);
    const drawText = (
      p: typeof page,
      t: string,
      opts: Parameters<typeof page.drawText>[1],
    ) => p.drawText(sanitizePdfText(t), opts);

    const hex = (data.accentHex || "#0B2545").replace("#", "");
    const r = parseInt(hex.slice(0, 2), 16) / 255 || 0;
    const g = parseInt(hex.slice(2, 4), 16) / 255 || 0.15;
    const b = parseInt(hex.slice(4, 6), 16) / 255 || 0.27;
    const accent = rgb(r, g, b);
    const ink = rgb(0.12, 0.12, 0.14);
    const soft = rgb(0.35, 0.35, 0.38);

    const marginX = 56;
    const marginTop = 60;
    const marginBottom = 60;
    let y = height - marginTop;

    // Accent bar
    page.drawRectangle({ x: 0, y: height - 8, width, height: 8, color: accent });

    // Header — candidate name & contact
    drawText(page, data.letter.candidateName, {
      x: marginX,
      y,
      size: 18,
      font: bold,
      color: accent,
    });
    y -= 20;
    if (data.letter.candidateContact) {
      drawText(page, data.letter.candidateContact, {
        x: marginX,
        y,
        size: 10,
        font,
        color: soft,
      });
      y -= 14;
    }
    y -= 8;
    page.drawLine({
      start: { x: marginX, y },
      end: { x: width - marginX, y },
      thickness: 0.6,
      color: soft,
    });
    y -= 24;

    // Date & "to" block. We deliberately mention the recipient exactly ONCE
    // — via the salutation below — so we skip drawing the recipient name
    // line here (which used to duplicate "Dear X," with an "X" header).
    if (data.letter.date) {
      drawText(page, data.letter.date, { x: marginX, y, size: 10.5, font, color: ink });
      y -= 22;
    }
    if (data.letter.employer) {
      drawText(page, data.letter.employer, { x: marginX, y, size: 11, font: bold, color: ink });
      y -= 20;
    }

    // Salutation (this is the ONE mention of the recipient by name).
    drawText(page, data.letter.salutation, { x: marginX, y, size: 11, font, color: ink });
    y -= 20;

    // RE: line — draw exactly once. If the AI-generated body already opens
    // with a "RE:" line we strip it so the header RE isn't duplicated.
    let bodyText = data.letter.body || "";
    const leadingRe = bodyText.match(/^\s*RE\s*:[^\n]*\n+/i);
    if (leadingRe) bodyText = bodyText.slice(leadingRe[0].length);
    if (data.letter.roleTitle) {
      const reLine = `RE: Application for ${data.letter.roleTitle}${
        data.letter.employer ? ` — ${data.letter.employer}` : ""
      }`;
      drawText(page, reLine.slice(0, 110), {
        x: marginX,
        y,
        size: 11,
        font: bold,
        color: accent,
      });
      y -= 22;
    }

    // Body: word-wrap paragraphs with full justification (except last line).
    const maxWidth = width - marginX * 2;
    const size = 11;
    const lineHeight = 15;
    const paragraphs = bodyText.split(/\n\s*\n/);

    function wrapWords(text: string): string[][] {
      const words = text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
      const lines: string[][] = [];
      let line: string[] = [];
      for (const w of words) {
        const candidate = line.length ? [...line, w] : [w];
        const width = font.widthOfTextAtSize(sanitizePdfText(candidate.join(" ")), size);
        if (width > maxWidth && line.length) {
          lines.push(line);
          line = [w];
        } else {
          line = candidate;
        }
      }
      if (line.length) lines.push(line);
      return lines;
    }

    function ensureSpace(needed: number) {
      if (y - needed < marginBottom) {
        page = doc.addPage([595.28, 841.89]);
        y = height - marginTop;
      }
    }

    function drawJustified(words: string[], justify: boolean) {
      if (!words.length) return;
      const text = words.join(" ");
      const naturalW = font.widthOfTextAtSize(sanitizePdfText(text), size);
      if (!justify || words.length < 2 || naturalW >= maxWidth || naturalW < maxWidth * 0.6) {
        drawText(page, text, { x: marginX, y, size, font, color: ink });
        return;
      }
      const wordsW = words.reduce(
        (s, w) => s + font.widthOfTextAtSize(sanitizePdfText(w), size),
        0,
      );
      const gapW = (maxWidth - wordsW) / (words.length - 1);
      let cursor = marginX;
      for (const w of words) {
        drawText(page, w, { x: cursor, y, size, font, color: ink });
        cursor += font.widthOfTextAtSize(sanitizePdfText(w), size) + gapW;
      }
    }

    for (const para of paragraphs) {
      const lines = wrapWords(para);
      lines.forEach((words, idx) => {
        ensureSpace(lineHeight);
        drawJustified(words, idx < lines.length - 1);
        y -= lineHeight;
      });
      y -= 8;
    }


    // Sign-off
    ensureSpace(60);
    y -= 8;
    drawText(page, data.letter.sign_off, { x: marginX, y, size: 11, font, color: ink });
    y -= 34;
    drawText(page, data.letter.candidateName, { x: marginX, y, size: 12, font: bold, color: accent });

    // Optionally append the candidate's CV (PDF or image) after the letter.
    if (data.cvAttachment) {
      try {
        const bin = atob(data.cvAttachment.base64);
        const cvBytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) cvBytes[i] = bin.charCodeAt(i);
        const mime = data.cvAttachment.mime.toLowerCase();
        if (mime === "application/pdf") {
          const cvDoc = await PDFDocument.load(cvBytes, { ignoreEncryption: true });
          const copied = await doc.copyPages(cvDoc, cvDoc.getPageIndices());
          copied.forEach((p) => doc.addPage(p));
        } else if (mime === "image/png" || mime === "image/jpeg" || mime === "image/jpg") {
          const img = mime === "image/png"
            ? await doc.embedPng(cvBytes)
            : await doc.embedJpg(cvBytes);
          const pageW = 595.28;
          const pageH = 841.89;
          const p = doc.addPage([pageW, pageH]);
          const margin = 36;
          const maxW = pageW - margin * 2;
          const maxH = pageH - margin * 2;
          const scale = Math.min(maxW / img.width, maxH / img.height, 1);
          const w = img.width * scale;
          const h = img.height * scale;
          p.drawImage(img, {
            x: (pageW - w) / 2,
            y: (pageH - h) / 2,
            width: w,
            height: h,
          });
        }
      } catch (e) {
        console.error("cv merge failed", (e as Error)?.message);
        // Fall through — still return the letter PDF alone.
      }
    }

    // Append any supporting documents (certificates, IDs, references) in order.
    async function appendFile(mime: string, base64: string) {
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const m = mime.toLowerCase();
      if (m === "application/pdf") {
        const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const copied = await doc.copyPages(src, src.getPageIndices());
        copied.forEach((p) => doc.addPage(p));
      } else if (m === "image/png" || m === "image/jpeg" || m === "image/jpg") {
        const img = m === "image/png" ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
        const pageW = 595.28;
        const pageH = 841.89;
        const p = doc.addPage([pageW, pageH]);
        const margin = 36;
        const scale = Math.min((pageW - margin * 2) / img.width, (pageH - margin * 2) / img.height, 1);
        const w = img.width * scale;
        const h = img.height * scale;
        p.drawImage(img, { x: (pageW - w) / 2, y: (pageH - h) / 2, width: w, height: h });
      }
    }

    if (Array.isArray(data.attachments)) {
      for (const att of data.attachments) {
        try {
          await appendFile(att.mime, att.base64);
        } catch (e) {
          console.error("attachment merge failed", (e as Error)?.message);
        }
      }
    }

    const bytes = await doc.save();

    let bin = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)) as any);
    }
    const base64 = btoa(bin);
    const safeName = data.filename
      ? data.filename
      : `${data.letter.candidateName.replace(/\s+/g, "_")}_Motivational_Letter.pdf`;
    return { base64, filename: safeName, contentType: "application/pdf" };
  });
